import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ModuleRequestError } from '@wit-support/admin-client-core';
import { Button, ConfirmDialog, InlineNotice, cn, toast } from '@wit-support/ui';
import { CONTENT_NETWORK_LABEL_RU, Time, actorLabelRu, formatInZone, useAdminTimezone, } from '@wit-support/ui-domain';
import { Gauge, OctagonX, Pause, Play, ShieldAlert } from 'lucide-react';
import { resumeAll, resumeConnection, stopAll, stopConnection } from './api.js';
import { SOCIAL_DAILY_TOTAL_HINT_RU, SOCIAL_GLOBAL_RESUME_CONFIRM_RU, SOCIAL_GLOBAL_STOP_CONFIRM_RU, SOCIAL_LIMIT_KIND_ORDER, blockPauseActive, blockPauseResumeConfirm, blockedHint, blockedText, dailyTotalExhausted, dailyTotalText, describePauseReason, resumeConfirm, resumeSummary, usageExhausted, usageText, warmupText, } from './outcome-text.js';
import { byNetwork, networkName, siblingNetworkOf } from './network-text.js';
import { SOCIAL_KEY, describeSocialError } from './shared.js';
/*
 * Защита аккаунтов от блокировки (RULINGS-SAFETY): общий «Стоп всех
 * действий», плашка заблокированного аккаунта с «Возобновить», пауза,
 * прогрев и использованные лимиты. Решения принимает движок перед каждым
 * действием; экран только показывает и передаёт команды владельца.
 */
export const OWNER_ONLY_TEXT = 'Это может только владелец.';
/** 409 движка: «Возобновить» при действующей паузе 368 без подтверждения. */
const BLOCK_CONFIRM_REQUIRED = 'social_block_confirm_required';
/** Ошибка команды владельца словами: 403 — «только владелец», остальное — общая фраза. */
export function ownerCommandError(err, network) {
    if (err instanceof ModuleRequestError && err.status === 403)
        return OWNER_ONLY_TEXT;
    return describeSocialError(err, 'write', network);
}
/**
 * Кнопка «Стоп всех действий» / «Возобновить все действия», всегда через
 * подтверждение. Права разные (FX-SS2): остановить — выключающее действие,
 * стоп-кран при инциденте, его жмёт и супервайзер (`social.manage`);
 * возобновить — только владелец (`social.connections_manage`), остальным
 * вместо кнопки подпись. Движок без поддержки общего стопа
 * (`globalStop: null`) — ничего: нажимать нечего.
 */
export function GlobalStopControl({ globalStop, canManage, canManageConnections, }) {
    const qc = useQueryClient();
    const [confirm, setConfirm] = useState(false);
    const stopped = globalStop?.stopped === true;
    const mut = useMutation({
        mutationFn: () => (stopped ? resumeAll() : stopAll()),
        onSuccess: (r) => {
            if (r.stopped)
                toast.success('Все действия остановлены');
            else
                toast.success('Действия возобновлены');
            void qc.invalidateQueries({ queryKey: SOCIAL_KEY });
        },
        onError: (err) => toast.error(ownerCommandError(err)),
    });
    if (globalStop === null)
        return null;
    const allowed = stopped ? canManageConnections : canManage || canManageConnections;
    if (!allowed) {
        // Супервайзер при включённом стопе: кнопки нет, но видно, кто снимает
        return stopped && canManage ? (_jsx("span", { className: "self-center text-xs text-[var(--color-text-muted)]", children: GLOBAL_RESUME_OWNER_ONLY_TEXT })) : null;
    }
    return (_jsxs(_Fragment, { children: [_jsxs(Button, { type: "button", variant: stopped ? 'secondary' : 'danger', disabled: mut.isPending, onClick: () => setConfirm(true), children: [stopped ? (_jsx(Play, { className: "mr-1 h-4 w-4", "aria-hidden": "true" })) : (_jsx(OctagonX, { className: "mr-1 h-4 w-4", "aria-hidden": "true" })), stopped ? 'Возобновить все действия' : 'Стоп всех действий'] }), _jsx(ConfirmDialog, { open: confirm, onOpenChange: setConfirm, title: stopped ? 'Возобновить все действия?' : 'Остановить все действия?', description: stopped ? SOCIAL_GLOBAL_RESUME_CONFIRM_RU : SOCIAL_GLOBAL_STOP_CONFIRM_RU, cancelLabel: "\u041E\u0442\u043C\u0435\u043D\u0430", actionLabel: stopped ? 'Возобновить' : 'Остановить всё', actionStyle: stopped ? 'primary' : 'danger', onConfirm: () => {
                    setConfirm(false);
                    mut.mutate();
                } })] }));
}
/** Подпись вместо «Возобновить все действия» у супервайзера. */
export const GLOBAL_RESUME_OWNER_ONLY_TEXT = 'Возобновить все действия может только владелец.';
/** Момент ещё не наступил (пауза действует). */
const future = (isoValue, now) => isoValue !== null && new Date(isoValue).getTime() > now;
/** Пауза ещё действует (сравнение с «сейчас»; прошедшая пауза — не показывается). */
export function pauseActive(s, now = Date.now()) {
    return future(s.pausedUntil, now);
}
/**
 * «Возобновить» пары: запрос, тост «что снято / что осталось» и перечитывание
 * состояния. Общий для {@link BlockedNotice} и {@link SiblingResumeButton}.
 */
function useResumeMutation(cell) {
    const qc = useQueryClient();
    const tz = useAdminTimezone();
    const mut = useMutation({
        mutationFn: (confirmBlock) => resumeConnection({ account: cell.account, network: cell.network }, confirmBlock ? { confirmBlock: true } : {}),
        onSuccess: (r) => {
            // Что снято и что осталось (RULINGS-SAFETY-2 M3): оставшиеся паузы
            // кнопка не снимает — человек должен видеть, почему действия ещё ждут.
            const { title, description } = resumeSummary(cell.account, r, (v) => formatInZone(v, 'dd.MM HH:mm', tz), cell.network);
            const opts = description ? { description, duration: 15_000 } : {};
            // что-то осталось (по перечню или по итоговому статусу движка) — не «успех»
            const pending = (r.remaining !== null && r.remaining.length > 0) ||
                (r.status != null && r.status !== 'active');
            if (pending)
                toast.info(title, opts);
            else
                toast.success(title, opts);
            void qc.invalidateQueries({ queryKey: SOCIAL_KEY });
        },
        onError: (err) => {
            toast.error(ownerCommandError(err, cell.network));
            // 409 social_block_confirm_required: экран не знал о паузе 368 —
            // перечитать состояние, тогда подтверждение станет строгим
            if (err instanceof ModuleRequestError && err.upstreamCode === BLOCK_CONFIRM_REQUIRED) {
                void qc.invalidateQueries({ queryKey: SOCIAL_KEY });
            }
        },
    });
    return mut;
}
/** Пауза пары действует только из-за соседней сети (FB-SPEC M3). */
export function siblingPauseActive(s, now = Date.now()) {
    if (s === null)
        return false;
    return ((s.pauseReason === 'blocked_sibling' && future(s.pausedUntil, now)) ||
        (s.replyPauseReason === 'spam_sibling' && future(s.replyPausedUntil, now)));
}
/** Подтверждение мягкое: снимается только пауза этой пары, соседняя сеть остаётся ограниченной. */
export function siblingResumeConfirm(network, sibling) {
    const other = sibling ? networkName(sibling) : 'соседняя сеть';
    return `Пауза поставлена, потому что ${other} этого аккаунта ограничена. Снять паузу только для ${networkName(network)}? Ограничение соседней сети останется.`;
}
/**
 * «Возобновить эту сеть» (FX-FB-S3): пара стоит только из-за ограничения
 * соседней сети (`blocked_sibling` / `spam_sibling`) — ни блокировки, ни
 * ручного стопа, поэтому {@link BlockedNotice} кнопки не даёт. Только
 * владелец; подтверждение мягкое, тело запроса `{}` (без `confirmBlock`).
 * Если на паре есть блокировка или ручной стоп — кнопку даёт BlockedNotice.
 */
export function SiblingResumeButton({ cell, canManageConnections, items = [], }) {
    const [confirm, setConfirm] = useState(false);
    const mut = useResumeMutation(cell);
    const s = cell.safety;
    if (!canManageConnections || s === null || !cell.supported)
        return null;
    if (s.blockedAt !== null || s.stoppedAt !== null)
        return null;
    if (!siblingPauseActive(s))
        return null;
    const reason = s.pauseReason === 'blocked_sibling' ? 'blocked_sibling' : 'spam_sibling';
    const sibling = siblingNetworkOf(cell, items, reason);
    return (_jsxs(_Fragment, { children: [_jsxs(Button, { type: "button", variant: "secondary", size: "sm", className: "self-start", disabled: mut.isPending, "aria-label": `Возобновить эту сеть: ${cell.account} · ${CONTENT_NETWORK_LABEL_RU[cell.network]}`, onClick: () => setConfirm(true), children: [_jsx(Play, { className: "mr-1 h-4 w-4", "aria-hidden": "true" }), "\u0412\u043E\u0437\u043E\u0431\u043D\u043E\u0432\u0438\u0442\u044C \u044D\u0442\u0443 \u0441\u0435\u0442\u044C"] }), _jsx(ConfirmDialog, { open: confirm, onOpenChange: setConfirm, title: `Возобновить ${cell.account} · ${networkName(cell.network)}?`, description: siblingResumeConfirm(cell.network, sibling), cancelLabel: "\u041E\u0442\u043C\u0435\u043D\u0430", actionLabel: "\u0412\u043E\u0437\u043E\u0431\u043D\u043E\u0432\u0438\u0442\u044C \u044D\u0442\u0443 \u0441\u0435\u0442\u044C", actionStyle: "primary", onConfirm: () => {
                    setConfirm(false);
                    mut.mutate(false);
                } })] }));
}
/**
 * Красная плашка «Instagram ограничил аккаунт — автоматика выключена» и кнопка
 * «Возобновить» (только владелец, с подтверждением «ограничение снято в
 * приложении Instagram»). Тот же «Возобновить» снимает и ручной стоп
 * аккаунта (жёлтая плашка, своё подтверждение). После — тост, какие паузы
 * сняты и какие остались (их кнопка не снимает), и в какой режим движок
 * вернул аккаунт (RULINGS-SAFETY-3 M1: после повторной блокировки —
 * «Вручную»).
 *
 * RULINGS-SAFETY-3 M2: если у аккаунта, кроме ручного стопа, действует пауза
 * после ограничения Instagram (368), «Возобновить» снимет и её — поэтому
 * подтверждение строгое («Статус аккаунта» в приложении Instagram), а запрос
 * уходит с `confirmBlock: true` (без него движок паузу 368 не снимает — 409
 * `social_block_confirm_required`; экран показывает фразу и перечитывает
 * состояние, после чего подтверждение станет строгим).
 */
export function BlockedNotice({ cell, canManageConnections, }) {
    const [confirm, setConfirm] = useState(false);
    const blockedAt = cell.safety?.blockedAt ?? null;
    const stoppedAt = cell.safety?.stoppedAt ?? null;
    const blocked = blockedAt !== null;
    const blockPause = blockPauseActive(cell.safety);
    // строгое подтверждение — снимаем ограничение Instagram (повторное или паузу 368)
    const strict = blocked || blockPause;
    const mut = useResumeMutation(cell);
    if (blockedAt === null && stoppedAt === null)
        return null;
    const blockPauseUntil = blockPause ? (cell.safety?.pausedUntil ?? null) : null;
    const who = actorLabelRu(cell.safety?.stoppedBy ?? null);
    return (_jsxs(InlineNotice, { variant: blocked ? 'danger' : 'warning', bordered: true, icon: blocked ? ShieldAlert : Pause, as: "div", role: "alert", children: [_jsxs("div", { className: "flex flex-col gap-2", children: [blocked ? (_jsxs(_Fragment, { children: [_jsxs("span", { children: [_jsx("span", { className: "font-medium", children: blockedText(cell.network) }), " (\u0441", ' ', _jsx(Time, { iso: blockedAt, preset: "shortNumeric" }), ")."] }), _jsx("span", { className: "text-[var(--color-text-primary)]", children: blockedHint(cell.network) })] })) : (_jsxs(_Fragment, { children: [_jsxs("span", { children: [_jsx("span", { className: "font-medium", children: "\u0414\u0435\u0439\u0441\u0442\u0432\u0438\u044F \u0430\u043A\u043A\u0430\u0443\u043D\u0442\u0430 \u043E\u0441\u0442\u0430\u043D\u043E\u0432\u043B\u0435\u043D\u044B \u0432\u0440\u0443\u0447\u043D\u0443\u044E" }), who ? ` (${who})` : '', ". \u041F\u043E\u043A\u0430 \u043D\u0435 \u043D\u0430\u0436\u043C\u0443\u0442 \u00AB\u0412\u043E\u0437\u043E\u0431\u043D\u043E\u0432\u0438\u0442\u044C\u00BB, \u043D\u0438\u0447\u0435\u0433\u043E \u043D\u0435 \u043E\u0442\u043F\u0440\u0430\u0432\u043B\u044F\u0435\u0442\u0441\u044F."] }), blockPause ? (_jsxs("span", { className: "text-[var(--color-text-primary)]", children: ["\u0415\u0449\u0451 \u0434\u0435\u0439\u0441\u0442\u0432\u0443\u0435\u0442 \u043F\u0430\u0443\u0437\u0430 \u043F\u043E\u0441\u043B\u0435 \u043E\u0433\u0440\u0430\u043D\u0438\u0447\u0435\u043D\u0438\u044F ", networkName(cell.network), " (\u043E\u0448\u0438\u0431\u043A\u0430 368)", blockPauseUntil ? (_jsxs(_Fragment, { children: [' ', "\u0434\u043E ", _jsx(Time, { iso: blockPauseUntil, preset: "shortNumeric" })] })) : null, ": \u00AB\u0412\u043E\u0437\u043E\u0431\u043D\u043E\u0432\u0438\u0442\u044C\u00BB \u0441\u043D\u0438\u043C\u0435\u0442 \u0438 \u0435\u0451 \u2014 ", blockPauseCheckText(cell.network)] })) : null] })), canManageConnections ? (_jsx(Button, { type: "button", variant: "secondary", size: "sm", className: "self-start", disabled: mut.isPending, "aria-label": `Возобновить: ${cell.account} · ${CONTENT_NETWORK_LABEL_RU[cell.network]}`, onClick: () => setConfirm(true), children: "\u0412\u043E\u0437\u043E\u0431\u043D\u043E\u0432\u0438\u0442\u044C" })) : (_jsx("span", { className: "text-xs", children: "\u0412\u043E\u0437\u043E\u0431\u043D\u043E\u0432\u0438\u0442\u044C \u043C\u043E\u0436\u0435\u0442 \u0442\u043E\u043B\u044C\u043A\u043E \u0432\u043B\u0430\u0434\u0435\u043B\u0435\u0446." }))] }), _jsx(ConfirmDialog, { open: confirm, onOpenChange: setConfirm, title: `Возобновить ${cell.account}?`, description: blocked
                    ? resumeConfirm(cell.network)
                    : blockPause
                        ? blockPauseResumeConfirm(cell.network)
                        : SOCIAL_GLOBAL_RESUME_CONFIRM_RU, cancelLabel: "\u041E\u0442\u043C\u0435\u043D\u0430", actionLabel: strict ? 'Ограничение снято — возобновить' : 'Возобновить', actionStyle: "primary", onConfirm: () => {
                    setConfirm(false);
                    mut.mutate(strict);
                } })] }));
}
/** Хвост строки о паузе 368 на жёлтой плашке: где проверить, что ограничение снято. */
export function blockPauseCheckText(network) {
    return byNetwork(network, 'нажимайте, только когда в приложении Instagram («Статус аккаунта») ограничение снято.', 'нажимайте, только когда на Странице в Facebook или в Meta Business Suite ограничение снято.');
}
export const STOP_ACCOUNT_CONFIRM_RU = 'Сердечки, ответы, скрытие и удаление от этого аккаунта перестанут уходить в соцсеть — и ручные, и от алгоритма. Комментарии продолжат собираться. Снять остановку сможет только владелец кнопкой «Возобновить».';
/**
 * То же, когда у аккаунта идёт пауза после ограничения Instagram (368): сбор
 * комментариев в это время тоже стоит (RULINGS-SAFETY-3 M3), а остановка
 * держит действия и после конца паузы — до «Возобновить» владельцем.
 */
export const STOP_ACCOUNT_BLOCK_PAUSE_CONFIRM_RU = 'Сейчас у аккаунта пауза после ограничения Instagram (ошибка 368): действия и сбор комментариев стоят до её конца. Остановка нужна, если в приложении Instagram («Статус аккаунта») ограничение дольше паузы: тогда и после паузы сердечки, ответы, скрытие и удаление не пойдут — ни ручные, ни от алгоритма. Снять остановку сможет только владелец кнопкой «Возобновить».';
/** То же для Страницы Facebook: где проверять ограничение — на Странице или в Meta Business Suite. */
export const STOP_ACCOUNT_FB_BLOCK_PAUSE_CONFIRM_RU = 'Сейчас у Страницы пауза после ограничения Facebook (ошибка 368): действия и сбор комментариев стоят до её конца. Остановка нужна, если на Странице в Facebook или в Meta Business Suite ограничение дольше паузы: тогда и после паузы сердечки, ответы, скрытие и удаление не пойдут — ни ручные, ни от алгоритма. Снять остановку сможет только владелец кнопкой «Возобновить».';
/**
 * «Остановить аккаунт» (RULINGS-SAFETY-2) — выключающее действие, доступно
 * супервайзеру и владельцу (`social.manage`), всегда через подтверждение.
 * Аккаунт уже остановлен или заблокирован — кнопки нет (там «Возобновить»).
 * Движок без защиты (`safety: null`) — кнопки нет: остановить там нечем.
 */
export function StopAccountButton({ cell, canManage, }) {
    const qc = useQueryClient();
    const [confirm, setConfirm] = useState(false);
    const mut = useMutation({
        mutationFn: () => stopConnection({ account: cell.account, network: cell.network }),
        onSuccess: () => {
            toast.success(`${cell.account}: действия остановлены`);
            void qc.invalidateQueries({ queryKey: SOCIAL_KEY });
        },
        onError: (err) => toast.error(describeSocialError(err, 'write')),
    });
    const s = cell.safety;
    if (!canManage || !cell.supported || s === null)
        return null;
    if (s.blockedAt !== null || s.stoppedAt !== null)
        return null;
    const label = `${cell.account} · ${CONTENT_NETWORK_LABEL_RU[cell.network]}`;
    return (_jsxs(_Fragment, { children: [_jsxs(Button, { type: "button", variant: "ghost", size: "sm", className: "self-start", disabled: mut.isPending, "aria-label": `Остановить аккаунт: ${label}`, onClick: () => setConfirm(true), children: [_jsx(Pause, { className: "mr-1 h-4 w-4", "aria-hidden": "true" }), "\u041E\u0441\u0442\u0430\u043D\u043E\u0432\u0438\u0442\u044C \u0430\u043A\u043A\u0430\u0443\u043D\u0442"] }), _jsx(ConfirmDialog, { open: confirm, onOpenChange: setConfirm, title: `Остановить действия ${label}?`, description: blockPauseActive(s)
                    ? byNetwork(cell.network, STOP_ACCOUNT_BLOCK_PAUSE_CONFIRM_RU, STOP_ACCOUNT_FB_BLOCK_PAUSE_CONFIRM_RU)
                    : STOP_ACCOUNT_CONFIRM_RU, cancelLabel: "\u041E\u0442\u043C\u0435\u043D\u0430", actionLabel: "\u041E\u0441\u0442\u0430\u043D\u043E\u0432\u0438\u0442\u044C", actionStyle: "danger", onConfirm: () => {
                    setConfirm(false);
                    mut.mutate();
                } })] }));
}
/**
 * Почему действия на паузе: фраза движка, иначе своя по коду. `network` —
 * сеть пары, `sibling` — сеть-источник паузы «из-за другой сети»
 * (`blocked_sibling`, FB-SPEC M3).
 */
export function pauseTextOf(s, network, sibling = null) {
    return s.pauseText ?? describePauseReason(s.pauseReason, network, sibling);
}
/**
 * Паузы строками: все действия, только ответы, только сбор комментариев;
 * режим «Выключено» — очередь стоит. Блокировку и ручной стоп показывает
 * {@link BlockedNotice}; прошедшие паузы не показываются.
 */
export function PauseLine({ safety, now = Date.now(), network, sibling = null, }) {
    const lines = [];
    if (safety.blockedAt === null && safety.stoppedAt === null) {
        if (future(safety.pausedUntil, now)) {
            lines.push({
                key: 'all',
                until: safety.pausedUntil,
                text: pauseTextOf(safety, network, sibling),
            });
        }
        if (safety.status === 'mode_off') {
            lines.push({
                key: 'mode',
                until: null,
                text: 'Режим «Выключено» — подтверждённые действия ждут, пока режим не включат',
            });
        }
    }
    if (future(safety.replyPausedUntil, now)) {
        lines.push({
            key: 'reply',
            until: safety.replyPausedUntil,
            text: safety.replyPauseText ??
                describePauseReason(safety.replyPauseReason ?? 'spam_replies', network, safety.siblingNetwork ?? sibling),
        });
    }
    if (future(safety.pollPausedUntil, now)) {
        lines.push({
            key: 'poll',
            until: safety.pollPausedUntil,
            text: safety.pollPauseText ?? describePauseReason('graph_load_poll', network),
        });
    }
    if (lines.length === 0)
        return null;
    return (_jsx(_Fragment, { children: lines.map((l) => (_jsxs("span", { className: "text-xs text-[var(--color-state-warning-fg)]", children: [l.until ? (_jsxs(_Fragment, { children: ["\u0414\u043E ", _jsx(Time, { iso: l.until, preset: "shortNumeric" }), ":", ' '] })) : null, l.text] }, l.key))) }));
}
/** «Использовано: ответов 3 из 15 в час · 7 из 60 за сутки» — по видам, ответы первыми. */
export function UsageLines({ safety, kinds = SOCIAL_LIMIT_KIND_ORDER, }) {
    const rows = kinds.flatMap((k) => {
        const u = safety.limits[k];
        return u ? [{ k, u }] : [];
    });
    const daily = safety.dailyTotal;
    if (rows.length === 0 && daily === null)
        return null;
    return (_jsxs("ul", { className: "flex flex-col gap-0.5 text-xs", "aria-label": "\u0418\u0441\u043F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u043D\u043E \u0438\u0437 \u043B\u0438\u043C\u0438\u0442\u043E\u0432", children: [rows.map(({ k, u }) => (_jsxs("li", { className: cn(usageExhausted(u)
                    ? 'text-[var(--color-state-warning-fg)]'
                    : 'text-[var(--color-text-muted)]'), children: ["\u0418\u0441\u043F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u043D\u043E ", usageText(k, u), usageExhausted(u) ? ' — лимит исчерпан, действия подождут' : ''] }, k))), daily ? _jsx(DailyTotalLine, { daily: daily }) : null] }));
}
/**
 * «Использовано за сутки (общий): 40 из 150» — общий суточный лимит всех
 * действий поверх лимитов по виду (FX-SE L5); исчерпан — до какого времени
 * действия ждут (00:00 UTC в часовом поясе админки).
 */
function DailyTotalLine({ daily }) {
    const exhausted = dailyTotalExhausted(daily);
    return (_jsxs("li", { title: daily.text ?? SOCIAL_DAILY_TOTAL_HINT_RU, className: cn(exhausted ? 'text-[var(--color-state-warning-fg)]' : 'text-[var(--color-text-muted)]'), children: ["\u0418\u0441\u043F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u043D\u043E ", dailyTotalText(daily), exhausted ? (daily.resetsAt ? (_jsxs(_Fragment, { children: [' ', "\u2014 \u043B\u0438\u043C\u0438\u0442 \u0438\u0441\u0447\u0435\u0440\u043F\u0430\u043D, \u0434\u0435\u0439\u0441\u0442\u0432\u0438\u044F \u043F\u043E\u0434\u043E\u0436\u0434\u0443\u0442 \u0434\u043E", ' ', _jsx(Time, { iso: daily.resetsAt, preset: "shortNumeric" })] })) : (' — лимит исчерпан, действия подождут')) : null] }));
}
/** Прогрев — одной строкой; закончился — ничего. */
export function WarmupLine({ safety }) {
    const text = safety.warmup ? warmupText(safety.warmup) : null;
    if (!text)
        return null;
    return _jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: text });
}
/**
 * Всё о защите аккаунта × сети одним блоком: блокировка, пауза, прогрев,
 * использованные лимиты. Движок не отдаёт состояние (`safety: null`) — пусто.
 */
export function AccountSafety({ cell, canManage = false, canManageConnections, kinds, items = [], }) {
    const safety = cell.safety;
    if (!cell.supported || safety === null)
        return null;
    const sibling = siblingNetworkOf(cell, items, safety.pauseReason);
    return (_jsxs("div", { className: "flex flex-col gap-1.5", children: [_jsx(BlockedNotice, { cell: cell, canManageConnections: canManageConnections }), _jsx(PauseLine, { safety: safety, network: cell.network, sibling: sibling }), _jsx(SiblingResumeButton, { cell: cell, canManageConnections: canManageConnections, items: items }), _jsx(WarmupLine, { safety: safety }), _jsx(UsageLines, { safety: safety, ...(kinds ? { kinds } : {}) }), _jsx(StopAccountButton, { cell: cell, canManage: canManage })] }));
}
/**
 * «Комментарии»: сводка по аккаунтам над таблицей — сколько ответов и
 * сердечек уже ушло из лимита, кто на паузе или ограничен. Решать, отвечать ли
 * сейчас, человек должен до того, как нажмёт «Ответить». Фильтр по аккаунту
 * или сети сужает сводку. Движок без защиты (`safety: null`) — ничего.
 */
export function LimitsSummary({ items, account, network, }) {
    const cells = items.filter((c) => c.supported &&
        c.safety !== null &&
        (account === undefined || c.account === account) &&
        (network === undefined || c.network === network));
    if (cells.length === 0)
        return null;
    return (_jsx(InlineNotice, { variant: "muted", bordered: true, icon: Gauge, as: "div", "aria-label": "\u041B\u0438\u043C\u0438\u0442\u044B \u0434\u0435\u0439\u0441\u0442\u0432\u0438\u0439", children: _jsx("div", { className: "flex flex-col gap-1 text-[var(--color-text-primary)]", children: cells.map((c) => {
                const s = c.safety;
                const state = s.blockedAt !== null
                    ? blockedText(c.network)
                    : pauseActive(s)
                        ? pauseTextOf(s, c.network, siblingNetworkOf(c, items, s.pauseReason))
                        : null;
                const usage = ['reply', 'like'].flatMap((k) => {
                    const u = s.limits[k];
                    return u ? [usageText(k, u)] : [];
                });
                if (s.dailyTotal)
                    usage.push(dailyTotalText(s.dailyTotal));
                return (_jsxs("div", { className: "flex flex-wrap gap-x-2", children: [_jsxs("span", { className: "font-medium", children: [c.account, " \u00B7 ", CONTENT_NETWORK_LABEL_RU[c.network], ":"] }), usage.length > 0 ? (_jsxs("span", { className: "text-[var(--color-text-muted)]", children: ["\u0438\u0441\u043F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u043D\u043E ", usage.join('; ')] })) : null, state ? (_jsx("span", { className: s.blockedAt !== null
                                ? 'text-[var(--color-state-danger-fg)]'
                                : 'text-[var(--color-state-warning-fg)]', children: state })) : null] }, `${c.account}:${c.network}`));
            }) }) }));
}
//# sourceMappingURL=safety.js.map